// src/components/dashboard/ReasonStatistics.jsx
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { API_GET_NOT_INTEREST_ENQUIRIES } from '../../Util/URLConstants';

// Chart.js imports
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import MUITypography from '../../Components/MUITypography';
import { align_column, vmPageTitleSx } from '../../Styles/VMStyles';

// Register ChartJS components
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, ChartTooltip, Legend);

const ReasonStatistics = ({ defaultFrom, defaultTo, userID,subscriptionStatus }) => {
  const theme = useTheme();
  const [fromDate, setFromDate] = useState(defaultFrom || '');
  const [toDate, setToDate] = useState(defaultTo || '');
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // keep a ref to current axios cancel token so we can cancel in-flight requests
  const cancelTokenRef = useRef(null);

  // seed from dashboard defaults
  useEffect(() => {
    if (defaultFrom) setFromDate(defaultFrom);
  }, [defaultFrom]);

  useEffect(() => {
    if (defaultTo) setToDate(defaultTo);
  }, [defaultTo]);

  const fetchReasons = useCallback(async () => {
    // cancel previous request if any
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Operation canceled due to new request.');
    }
    cancelTokenRef.current = axios.CancelToken.source();

    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(API_GET_NOT_INTEREST_ENQUIRIES, {
        params: {
          from: fromDate || undefined,
          to: toDate || undefined,
          user_id: userID ?? undefined,
        },
        cancelToken: cancelTokenRef.current.token,
      });

      const payload = res?.data;
      // payload may be { data: [...] } or the array directly
      const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : payload?.data ?? []);

      const normalized = Array.isArray(list)
        ? list.map((r) => ({
            reason: r?.reason ?? r?.label ?? '—',
            count: Number(r?.count ?? 0),
          }))
        : [];

      normalized.sort((a, b) => b.count - a.count);
      setReasons(normalized);
    } catch (err) {
      if (axios.isCancel(err)) {
        // request was cancelled - do nothing
        return;
      }
      console.error('ReasonStatistics error:', err);
      setError('Failed to load data');
      setReasons([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, userID]);

  // fetch when filters or user change
  useEffect(() => {
    fetchReasons();

    return () => {
      // cancel any inflight request if component unmounts / deps change
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted or deps changed.');
      }
    };
  }, [fetchReasons]);

  // const maxCount = useMemo(
  //   () => (reasons.length ? Math.max(...reasons.map((r) => r.count || 0)) : 1),
  //   [reasons]
  // );

  const totalCount = useMemo(() => reasons.reduce((s, r) => s + (r.count || 0), 0), [reasons]);

  // Prepare chart data for a horizontal bar chart (indexAxis: 'y')
  const chartData = useMemo(() => {
    const labels = reasons.map((r) => r.reason);
    const data = reasons.map((r) => r.count);

    // derive background colors from theme.primary.main (chart.js accepts CSS color strings)
    const base = theme?.palette?.primary?.main ?? '#1976d2';
    const bgColors = data.map(() => base);

    return {
      labels,
      datasets: [
        {
          label: 'Occurrences',
          data,
          backgroundColor: bgColors,
          borderRadius: 6,
          barThickness: 18,
          maxBarThickness: 28,
        },
      ],
    };
  }, [reasons, theme?.palette?.primary?.main]);

  const chartOptions = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              // for horizontal bars parsed.x contains the value
              const parsed = context?.parsed;
              const val = typeof parsed === 'object' ? parsed.x ?? parsed : parsed ?? 0;
              const pct = totalCount ? ((val / totalCount) * 100).toFixed(1) : '0.0';
              return `${Number(val).toLocaleString()} — ${pct}% of total`;
            },
          },
        },
        title: { display: false },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            callback: (val) => (typeof val === 'number' ? val.toLocaleString() : val),
          },
          grid: {
            borderDash: [4, 4],
          },
        },
        y: {
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            callback: (value) => {
              const label = typeof value === 'string' ? value : String(value);
              return label.length > 28 ? `${label.slice(0, 25)}...` : label;
            },
          },
          grid: { display: false },
        },
      },
      animation: { duration: 350 },
    }),
    [totalCount]
  );

  return (
    <Card sx={{ boxShadow: 1 ,border:'1px solid #E0E0E0'}}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ ...align_column, alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <MUITypography variant="pageTitle" sx={{ ...vmPageTitleSx, mb: 1 }}> Lost Customers Stats</MUITypography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : reasons.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No reason data available for selected range.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing top {reasons.length} reasons — total occurrences: {totalCount.toLocaleString()}
              </Typography>
            </Box>

            <Box sx={{ mb: 2, height: Math.max(260, reasons.length * 48) }}>
              <Bar data={chartData} options={chartOptions} aria-label="Reason statistics bar chart" role="img" />
            </Box>

            {/* small legend / accessibility listing beneath the chart */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {reasons.map((r, idx) => {
                const percentageOfTotal = totalCount ? ((r.count / totalCount) * 100).toFixed(1) : '0.0';
                return (
                  <Box
                    key={`${r.reason ?? 'reason'}-${idx}`}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}
                  >
                    <Tooltip title={r.reason} placement="top">
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          maxWidth: 520,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.reason}
                      </Typography>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                      {r.count.toLocaleString()} ({percentageOfTotal}%)
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReasonStatistics;
